import { FC } from 'react';
import { useIntl } from 'react-intl';
import { compact, isEmpty } from 'lodash';
import { Tabs as EbscoTabs } from '@ebsco-ui/ebsco-ui';

import { SearchInstanceDto, SectionWithTitle } from '@app/components';
import { useFeatureFlag } from '@app/hooks';
import { getTabsI18n } from '@app/utils';
import { Feature } from '@app/constants';

import { getMarcDownloadFormats } from './utils';
import { QuickMarcView } from './QuickMarcView';
import { RecordMetadata } from './RecordMetadata';
import { useInstanceSections } from './useInstanceSections';

export enum INSTANCE_DETAILS_TABS {
  fullRecord,
  marcRecord,
}

interface InstanceDetailsTabsProps {
  instance: SearchInstanceDto;
  LCLinkResolverLink?: string;
  onTabChange: (value: INSTANCE_DETAILS_TABS) => void;
}

export const Tabs: FC<InstanceDetailsTabsProps> = ({
  instance = {} as SearchInstanceDto,
  LCLinkResolverLink,
  onTabChange,
}) => {
  const intl = useIntl();
  const { $t } = intl;
  const staffViewFlag = useFeatureFlag({ name: Feature.staffView });
  const shouldDisplayMarc =
    staffViewFlag.isActive && instance?.source?.includes('MARC');
  const sections = useInstanceSections({ instance, LCLinkResolverLink });

  const handleTabChange = (index: INSTANCE_DETAILS_TABS) => {
    onTabChange(index);
  };

  return (
    <EbscoTabs
      tabs={compact([
        {
          title: $t({
            id: 'instanceDetails.fullRecord',
            defaultMessage: 'Full record',
          }),
        },
        shouldDisplayMarc && {
          title: $t({
            id: 'instanceDetails.marcRecord',
            defaultMessage: 'MARC record',
          }),
        },
      ])}
      i18n={getTabsI18n(intl)}
      onSelect={handleTabChange}
    >
      <EbscoTabs.TabPanel>
        {sections.map(({ title, items }) => {
          if (isEmpty(items)) {
            return null;
          }

          return (
            <SectionWithTitle key={title} title={title}>
              <RecordMetadata items={items} />
            </SectionWithTitle>
          );
        })}
      </EbscoTabs.TabPanel>
      {shouldDisplayMarc && (
        <EbscoTabs.TabPanel>
          <QuickMarcView
            downloadFormats={getMarcDownloadFormats(instance.lccnPermalinks)}
          />
        </EbscoTabs.TabPanel>
      )}
    </EbscoTabs>
  );
};
